<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email, helpers } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import FormInput from '../../../../../components/Form/Input.vue';
import PasswordInput from '../../../../../components/Form/PasswordInput.vue';
import SubmitButton from '../../../../../components/Button/SubmitButton.vue';
import { isValidPassword, isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import GoogleOAuthButton from '../../../../../components/GoogleOauth/Button.vue';
import PhoneInput from 'shared/components/PhoneInput.vue';
import { register } from '../../../../../api/auth';
// import * as CompanyEmailValidator from 'company-email-validator';

export default {
  components: {
    FormInput,
    PasswordInput,
    GoogleOAuthButton,
    SubmitButton,
    VueHcaptcha,
    PhoneInput,
  },
  mixins: [globalConfigMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        accountName: '',
        fullName: '',
        email: '',
        password: '',
        phoneNumber: '',
        countryCode: '',
        dialCode: '',
        referrerCode: '',
        hCaptchaClientResponse: '',
      },
      isReferrerCodeLocked: false,
      didCaptchaReset: false,
      isSignupInProgress: false,
      error: '',
    };
  },
  validations() {
    return {
      credentials: {
        accountName: {
          required,
          minLength: minLength(2),
        },
        fullName: {
          required,
          minLength: minLength(2),
        },
        email: {
          required,
          email,
          // businessEmailValidator(value) {
          //   return CompanyEmailValidator.isCompanyEmail(value);
          // },
        },
        password: {
          required,
          minLength: minLength(6),
          isValidPassword,
        },
        phoneNumber: {
          required,
        },
        countryCode: {
          required,
        },
        referrerCode: {
          referenceCodeFormat: helpers.withMessage(
            this.$t('REGISTER.REFERRER_CODE.ERROR'),
            value => !value || /^[A-Za-z0-9]{4,16}$/i.test(value)
          ),
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    termsLink() {
      return this.$t('REGISTER.TERMS_ACCEPT')
        .replace('https://www.hichat.asia/terms', this.globalConfig.termsURL)
        .replace(
          'https://www.hichat.asia/privacy-policy',
          this.globalConfig.privacyURL
        );
    },
    hasAValidCaptcha() {
      if (this.globalConfig.hCaptchaSiteKey) {
        return !!this.credentials.hCaptchaClientResponse;
      }
      return true;
    },
    passwordErrorText() {
      const { password } = this.v$.credentials;
      if (!password.$error) {
        return '';
      }
      
      const passwordValue = this.credentials.password;
      
      if (passwordValue.length < 6) {
        return this.$t('REGISTER.PASSWORD.ERROR');
      }
      
      if (passwordValue.length >= 6 && !isValidPassword(passwordValue)) {
        return this.$t('REGISTER.PASSWORD.IS_INVALID_PASSWORD');
      }
      
      return this.$t('REGISTER.PASSWORD.ERROR');
    },
    showGoogleOAuth() {
      return Boolean(window.hichatConfig.googleOAuthClientId);
    },
    phoneNumberErrorText() {
      const { phoneNumber, countryCode } = this.v$.credentials;
      
      // Show error if either field has been touched (blurred) and is invalid
      if (!phoneNumber.$dirty && !countryCode.$dirty) {
        return '';
      }
      
      if (!this.credentials.countryCode && !this.credentials.phoneNumber) {
        return this.$t('GENERAL.PHONE_INPUT.SELECT_COUNTRY_AND_PHONE');
      }
      
      if (this.credentials.countryCode && !this.credentials.phoneNumber) {
        return this.$t('GENERAL.PHONE_INPUT.ENTER_PHONE');
      }
      
      if (!this.credentials.countryCode && this.credentials.phoneNumber) {
        return this.$t('GENERAL.PHONE_INPUT.SELECT_COUNTRY');
      }
      
      return '';
    },
    referrerCodeErrorText() {
      const { referrerCode } = this.v$.credentials;
      if (!referrerCode.$error) {
        return '';
      }
      if (!referrerCode.referenceCodeFormat) {
        return this.$t('REGISTER.REFERRER_CODE.ERROR');
      }
      return '';
    },
  },
  mounted() {
    this.initializeReferrerCode();
  },
  methods: {
    initializeReferrerCode() {
      const urlCode = this.getFormattedReferrerCode(this.readReferrerCodeFromUrl());
      if (urlCode) {
        this.setLockedReferrerCode(urlCode);
        this.storeReferrerCode(urlCode);
        return;
      }

      const storedCode = this.getStoredReferrerCode();
      if (storedCode) {
        this.setLockedReferrerCode(storedCode);
      }
    },
    readReferrerCodeFromUrl() {
      try {
        const params = new URLSearchParams(window.location.search);
        return params.get('ref');
      } catch (error) {
        return null;
      }
    },
    setLockedReferrerCode(code) {
      const formatted = this.formatReferrerCode(code);
      this.credentials.referrerCode = formatted;
      this.isReferrerCodeLocked = true;
      if (this.v$?.credentials?.referrerCode) {
        this.v$.credentials.referrerCode.$reset();
      }
    },
    storeReferrerCode(code) {
      const formatted = this.formatReferrerCode(code);
      if (!this.isValidReferrerCode(formatted)) {
        return;
      }
      try {
        localStorage.setItem('signupReferrerCode', formatted);
      } catch (error) {
        // ignore storage errors
      }
    },
    getStoredReferrerCode() {
      try {
        const stored = localStorage.getItem('signupReferrerCode');
        const formatted = this.getFormattedReferrerCode(stored);
        if (!formatted && stored) {
          localStorage.removeItem('signupReferrerCode');
        }
        return formatted;
      } catch (error) {
        return null;
      }
    },
    removeStoredReferrerCode() {
      try {
        localStorage.removeItem('signupReferrerCode');
      } catch (error) {
        // ignore storage errors
      }
      this.isReferrerCodeLocked = false;
    },
    formatReferrerCode(value) {
      return (value || '').toString().trim().toUpperCase();
    },
    isValidReferrerCode(value) {
      if (!value) {
        return false;
      }
      return /^[A-Z0-9]{4,16}$/.test(value);
    },
    getFormattedReferrerCode(code) {
      if (!code) {
        return null;
      }
      const formatted = this.formatReferrerCode(code);
      return this.isValidReferrerCode(formatted) ? formatted : null;
    },
    async submit() {
      console.log('Submit button clicked');
      console.log('Credentials before format:', this.credentials);

      // Format phone number before validation
      this.formatPhoneNumber();
      this.normalizeReferrerCode();
      console.log('Credentials after format:', this.credentials);
      
      this.v$.$touch();
      console.log('Validation errors:', this.v$.$error);
      console.log('Is invalid:', this.v$.$invalid);
      
      if (this.v$.$invalid) {
        console.log('Form is invalid, stopping submission');
        this.resetCaptcha();
        return;
      }
      
      console.log('Form is valid, proceeding with registration');
      this.isSignupInProgress = true;
      try {
        await register(this.credentials);
        console.log('Registration successful');
        this.removeStoredReferrerCode();
        window.location = DEFAULT_REDIRECT_URL;
      } catch (error) {
        console.error('Registration error:', error);
        let errorMessage =
          error?.message || this.$t('REGISTER.API.ERROR_MESSAGE');
        this.resetCaptcha();
        useAlert(errorMessage, {type: 'error'});
      } finally {
        this.isSignupInProgress = false;
      }
    },
    onRecaptchaVerified(token) {
      this.credentials.hCaptchaClientResponse = token;
      this.didCaptchaReset = false;
    },
    resetCaptcha() {
      if (!this.globalConfig.hCaptchaSiteKey) {
        return;
      }
      this.$refs.hCaptcha.reset();
      this.credentials.hCaptchaClientResponse = '';
      this.didCaptchaReset = true;
    },
    setPhoneCode(code) {
      this.credentials.countryCode = code;
    },
    setDialCode(dialCode) {
      this.credentials.dialCode = dialCode;
    },
    onPhoneBlur() {
      this.v$.credentials.phoneNumber.$touch();
      this.v$.credentials.countryCode.$touch();
    },
    formatPhoneNumber() {
      // Ensure phone number is in E.164 format
      if (this.credentials.phoneNumber && !this.credentials.phoneNumber.startsWith('+')) {
        // If phone number doesn't start with +, add the dial code
        if (this.credentials.dialCode) {
          this.credentials.phoneNumber = this.credentials.dialCode + this.credentials.phoneNumber;
        }
      }
    },
    normalizeReferrerCode() {
      if (!this.credentials.referrerCode) {
        return;
      }
      this.credentials.referrerCode = this.formatReferrerCode(this.credentials.referrerCode);
    },
  },
};
</script>

<template>
  <div class="flex-1 px-1 overflow-auto">
    <form class="space-y-3" @submit.prevent="submit">
      <div class="grid grid-cols-2 gap-2">
        <FormInput
          v-model="credentials.fullName"
          name="full_name"
          class="flex-1"
          :class="{ error: v$.credentials.fullName.$error }"
          :label="$t('REGISTER.FULL_NAME.LABEL')"
          :placeholder="$t('REGISTER.FULL_NAME.PLACEHOLDER')"
          :has-error="v$.credentials.fullName.$error"
          :error-message="$t('REGISTER.FULL_NAME.ERROR')"
          @blur="v$.credentials.fullName.$touch"
        >
          <template #rightOfLabel>
            <span class="text-red-500">*</span>
          </template>
        </FormInput>
        <FormInput
          v-model="credentials.accountName"
          name="account_name"
          class="flex-1"
          :class="{ error: v$.credentials.accountName.$error }"
          :label="$t('REGISTER.COMPANY_NAME.LABEL')"
          :placeholder="$t('REGISTER.COMPANY_NAME.PLACEHOLDER')"
          :has-error="v$.credentials.accountName.$error"
          :error-message="$t('REGISTER.COMPANY_NAME.ERROR')"
          @blur="v$.credentials.accountName.$touch"
        >
          <template #rightOfLabel>
            <span class="text-red-500">*</span>
          </template>
        </FormInput>
      </div>
      <FormInput
        v-model="credentials.email"
        type="email"
        name="email_address"
        :class="{ error: v$.credentials.email.$error }"
        :label="$t('REGISTER.EMAIL.LABEL')"
        :placeholder="$t('REGISTER.EMAIL.PLACEHOLDER')"
        :has-error="v$.credentials.email.$error"
        :error-message="$t('REGISTER.EMAIL.ERROR')"
        @blur="v$.credentials.email.$touch"
      >
        <template #rightOfLabel>
          <span class="text-red-500">*</span>
        </template>
      </FormInput>
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          :class="{
            'text-red-500': v$.credentials.phoneNumber.$error,
          }"
        >
          {{ $t('REGISTER.PHONE_NUMBER.LABEL') }}
          <span class="text-red-500">*</span>
        </label>
        <PhoneInput
          v-model="credentials.phoneNumber"
          :value="credentials.phoneNumber"
          :error="phoneNumberErrorText !== ''"
          :error-message="phoneNumberErrorText"
          :placeholder="$t('REGISTER.PHONE_NUMBER.PLACEHOLDER')"
          @blur="onPhoneBlur"
          @set-code="setPhoneCode"
          @set-dial-code="setDialCode"
        />
      </div>
      <FormInput
        v-model="credentials.referrerCode"
        name="referrer_code"
        maxlength="16"
        autocomplete="off"
        :class="{ error: v$.credentials.referrerCode.$error }"
        :label="$t('REGISTER.REFERRER_CODE.LABEL')"
        :placeholder="$t('REGISTER.REFERRER_CODE.PLACEHOLDER')"
        :has-error="v$.credentials.referrerCode.$error"
        :error-message="referrerCodeErrorText"
        :disabled="isReferrerCodeLocked"
        @blur="v$.credentials.referrerCode.$touch"
      />
      <p class="text-xs text-slate-500 dark:text-slate-300 -mt-2">
        {{
          isReferrerCodeLocked
            ? $t('REGISTER.REFERRER_CODE.LOCKED_HELP')
            : $t('REGISTER.REFERRER_CODE.HELP')
        }}
      </p>
      <PasswordInput
        v-model="credentials.password"
        name="password"
        :class="{ error: v$.credentials.password.$error }"
        :label="$t('REGISTER.PASSWORD.LABEL')"
        :placeholder="$t('REGISTER.PASSWORD.PLACEHOLDER')"
        :has-error="v$.credentials.password.$error"
        :error-message="passwordErrorText"
        autocomplete="new-password"
        @blur="v$.credentials.password.$touch"
      >
        <template #rightOfLabel>
          <span class="text-red-500">*</span>
        </template>
      </PasswordInput>
      <div v-if="globalConfig.hCaptchaSiteKey" class="mb-3">
        <VueHcaptcha
          ref="hCaptcha"
          :class="{ error: !hasAValidCaptcha && didCaptchaReset }"
          :sitekey="globalConfig.hCaptchaSiteKey"
          @verify="onRecaptchaVerified"
        />
        <span
          v-if="!hasAValidCaptcha && didCaptchaReset"
          class="text-xs text-red-400"
        >
          {{ $t('SET_NEW_PASSWORD.CAPTCHA.ERROR') }}
        </span>
      </div>
      <SubmitButton
        button-class="!bg-hilab hover:opacity-90 h-[40px]"
        :button-text="$t('REGISTER.SUBMIT')"
        :disabled="isSignupInProgress || !hasAValidCaptcha"
        :loading="isSignupInProgress"
        icon-class="arrow-chevron-right"
      />
    </form>
    <GoogleOAuthButton v-if="showGoogleOAuth" class="flex-col-reverse">
      {{ $t('REGISTER.OAUTH.GOOGLE_SIGNUP') }}
    </GoogleOAuthButton>
    <p
      class="text-sm mb-1 mt-5 text-slate-800 dark:text-hichat-50 [&>a]:text-hichat-500 [&>a]:font-medium [&>a]:hover:text-hichat-600"
      v-html="termsLink"
    />
  </div>
</template>

<style scoped lang="scss">
.h-captcha--box {
  &::v-deep .error {
    iframe {
      border: 1px solid var(--r-500);
      border-radius: var(--border-radius-normal);
    }
  }
}
</style>
